import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { useTheme } from 'styled-components';
import PortalComponent from '../components/Portal.component';
import { useAppSelector } from './useAppSelector.hook';
import { getDispatchModal } from '../redux/App/App.slice';
import { MODAL_CONTENT } from '../constants/modalContent.constant';

export interface ModalManagerValue {
  /**
   * Opens a modal using the provided component and props
   */
  openModal: <T extends StackedModalProps, P extends T>(
    component: React.ComponentType<T>,
    props?: Omit<P, keyof StackedModalProps>,
    options?: OpenModalOptions,
  ) => any;

  /**
   * Closes the active modal
   */
  closeModal: () => void;

  /**
   * Closes the number of modals
   */
  closeModals: (amount?: number) => void;

  /**
   * Closes all modals
   */
  closeAllModals: () => void;

  stack: StackedModal[];
}

export type OpenModalOptions = {
  /**
   * Replaces the active modal in the stack
   */
  replace?: boolean;
  onExited?: Function;
  onClose?: Function;
  position?: any;
  fromDispatch?: boolean;
  disableClose?: boolean;
  needAllStatuses?: boolean;
};

export interface StackedModalProps {
  open?: boolean;
}

export type StackedModal = {
  component: React.ComponentType;
  props: any;
  options: any;
};

const ModalManagerContext = React.createContext<ModalManagerValue>({} as any);

export interface ModalManagerProps {
  children?: React.ReactNode;
}

const ModalManagerHook = ({ children }: ModalManagerProps) => {
  const [stack, setStack] = useState<StackedModal[]>([]);
  const theme = useTheme() as any;
  const dispatchModal = useAppSelector(getDispatchModal);

  useEffect(() => {
    MODAL_CONTENT[dispatchModal.type] &&
      value.openModal(
        MODAL_CONTENT[dispatchModal.type],
        {
          ...dispatchModal.data,
        },
        {
          fromDispatch: true,
        },
      );
  }, [dispatchModal]);

  const value = useMemo<ModalManagerValue>(() => {
    function pop(amount = 1) {
      return setStack(prev => [...prev].slice(0, prev.length - amount));
    }

    function dismissAll() {
      setStack([]);
    }

    function dismiss(amount?: number) {
      if (stack.length === 1) {
        dismissAll();
      } else {
        pop(amount);
      }
    }

    return {
      stack,
      openModal: (component, props, options) => {
        setStack(prev => {
          let newStack = [...prev];

          if (options && options.replace) {
            newStack = stack.slice(0, stack.length - 1);
          }

          const _props = { ...props, ...{ open: true } };
          return [
            ...newStack,
            { component, props: _props, options } as StackedModal,
          ];
        });
      },
      closeModal: () => dismiss(1),
      closeModals: dismiss,
      closeAllModals: dismissAll,
    };
  }, [stack]);

  useEffect(() => {
    if (
      (!dispatchModal || (dispatchModal && !dispatchModal.data)) &&
      stack.length > 0
    ) {
      const lastModal = stack[stack.length - 1];
      if (lastModal.options && lastModal.options.fromDispatch) {
        value.closeModal();
      }
    }
  }, [dispatchModal, value, stack]);

  return (
    <ModalManagerContext.Provider value={value}>
      {children}
      <TransitionGroup>
        {stack.map((modal, index) => {
          return (
            <Transition key={index} timeout={{ exit: 300 }}>
              {state => (
                <PortalComponent
                  withOverlay={true}
                  position={modal.options ? modal.options.position : undefined}
                  disableClose={modal.options?.disableClose}
                  onClose={() => {
                    modal.options?.onClose && modal.options.onClose();
                    !(modal.props.isBlocked || false) && value.closeModal();
                  }}
                  modalStyle={{
                    ...theme.animations.defaultState,
                    ...theme.animations.defaultSteps[state],
                  }}
                >
                  <modal.component key={index} {...modal.props} />
                </PortalComponent>
              )}
            </Transition>
          );
        })}
      </TransitionGroup>
    </ModalManagerContext.Provider>
  );
};

export default ModalManagerHook;

export function useModalManager() {
  return useContext(ModalManagerContext);
}
